import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Logo } from '@/components/Logo';

interface NavItem {
  label: string;
  to: string;
  primary?: boolean;
}

interface SiteHeaderProps {
  navItems?: NavItem[];
  showCTA?: boolean;
  ctaLabel?: string;
  ctaTo?: string;
  fixed?: boolean;
}

// Default navigation for PUBLIC (non-authenticated) visitors
// Note: Inspeções are NOT included - they are only accessible to registered users
const defaultNavItems: NavItem[] = [
  { label: 'Elevadores', to: '/elevadores' },
  { label: 'Como Funciona', to: '/como-funciona' },
  { label: 'Consultoria', to: '/planos-consultoria', primary: true },
  { label: 'Conteúdos Técnicos', to: '/analises-tecnicas-elevadores' },
];

export function SiteHeader({
  navItems = defaultNavItems,
  showCTA = true,
  ctaLabel = 'Criar Conta Gratuita',
  ctaTo = '/members/auth',
  fixed = false,
}: SiteHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const headerClass = fixed
    ? 'fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border'
    : 'sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border';

  return (
    <header className={headerClass}>
      <div className="container flex h-16 items-center justify-between w-full px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Logo to="/" />

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-4">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`text-sm transition-colors ${
                item.primary
                  ? 'text-primary font-semibold hover:text-primary/80'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {item.label}
            </Link>
          ))}
          
          {/* WhatsApp Icon Button */}
          <WhatsAppButton variant="icon" size="sm" />
          
<Button asChild variant="ghost" size="sm">
            <Link to="/members/auth?tab=login">Entrar</Link>
          </Button>
          {showCTA && (
            <Button asChild size="sm">
              <Link to={ctaTo}>{ctaLabel}</Link>
            </Button>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-foreground touch-manipulation active:bg-muted/50 rounded-lg transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-t border-border">
          <nav className="container py-4 flex flex-col gap-2">
            {navItems.map((item) => (
              <Button
                key={item.to}
                variant="ghost"
                className={`justify-start w-full ${item.primary ? 'text-primary font-semibold' : ''}`}
                onClick={() => {
                  navigate(item.to);
                  setMobileMenuOpen(false);
                }}
              >
                {item.label}
              </Button>
            ))}
            
{/* WhatsApp Button Mobile */}
            <WhatsAppButton 
              variant="button" 
              size="sm" 
              label="Falar no WhatsApp"
              className="w-full justify-center"
            />
            
            <Button
              variant="outline"
              className="w-full mt-2"
              onClick={() => {
                navigate('/members/auth?tab=login');
                setMobileMenuOpen(false);
              }}
            >
              Entrar
            </Button>
            
            {showCTA && (
              <Button
                className="w-full mt-2"
                onClick={() => {
                  navigate(ctaTo);
                  setMobileMenuOpen(false);
                }}
              >
                {ctaLabel}
              </Button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
